import React, { useState, useEffect } from 'react';
import { useTranslate, useNotify } from 'react-admin';
import { recommendationService, Recommendation } from '../../services/recommendationService';
import { useCourseEnrollment } from '../../hooks/useCourseEnrollment';
import { displayEnrollmentMode } from '../../utils/enrollmentMode';
import MaterialSymbol from '../common/MaterialSymbol';

interface RecommendedCoursesWidgetProps {
    limit?: number;
}

/**
 * "Empfohlen für Sie" — Empfehlungs-Karten im neuen Design (.reco/.rcard,
 * Hero einfarbig in var(--oa-primary), kein Gradient — Kunden-Feedback).
 *
 * Re-Skin von components/Recommendations/RecommendedCourses mit identischem
 * Verhalten (Issue #318): Datenabruf via recommendationService, Einschreibung
 * inline über useCourseEnrollment — kein "Alle anzeigen", keine Navigation
 * in die Kursverwaltung (/courses/*).
 */
const RecommendedCoursesWidget: React.FC<RecommendedCoursesWidgetProps> = ({ limit = 4 }) => {
    const translate = useTranslate();
    const raNotify = useNotify();

    const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const { enroll } = useCourseEnrollment({
        notify: (message, severity) => raNotify(message, { type: severity }),
    });

    useEffect(() => {
        const loadRecommendations = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await recommendationService.getRecommendations(limit);
                setRecommendations(data);
            } catch (err) {
                console.error('Failed to load recommendations:', err);
                setError(err instanceof Error ? err.message : 'Failed to load recommendations');
            } finally {
                setLoading(false);
            }
        };
        loadRecommendations();
    }, [limit]);

    const handleEnroll = (courseId: number) => {
        const rec = recommendations.find(r => r.course.id === courseId);
        if (rec) {
            enroll({
                id: rec.course.id,
                title: rec.course.title,
                enrollmentMode: rec.enrollmentMode,
                isLicensedReference: rec.isLicensedReference,
            });
        }
    };

    if (loading) {
        return (
            <div className="panel">
                <div className="skel" />
                <div className="skel" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="panel">
                <p className="empty-note">{error}</p>
            </div>
        );
    }

    if (recommendations.length === 0) {
        return null;
    }

    return (
        <div>
            <div className="sec-head">
                <h2 className="sec-title">
                    <span className="ico green"><MaterialSymbol name="star" size={19} /></span>
                    {translate('recommendations.title', { _: 'Empfohlen für Sie' })}
                </h2>
            </div>
            <div className="reco">
                {recommendations.map((rec) => {
                    const { course } = rec;
                    const primaryReason = recommendationService.getPrimaryReason(rec);
                    const hasThumb = Boolean(course.thumbnailUrl);

                    return (
                        <div className="rcard" key={course.id}>
                            <div
                                className={`hero${hasThumb ? ' thumb' : ''}`}
                                style={hasThumb ? { backgroundImage: `url(${course.thumbnailUrl})` } : undefined}
                            >
                                {!hasThumb && <MaterialSymbol name="school" size={52} />}
                                <span className="spark" title={rec.reasonLabels?.join(', ')}>
                                    <MaterialSymbol name="star" size={16} />
                                </span>
                            </div>
                            <div className="body">
                                <div className="tags">
                                    <span className="chip-out green">
                                        {recommendationService.getDifficultyLabel(course.difficulty)}
                                    </span>
                                    {course.catalog && <span className="chip-out">{course.catalog.name}</span>}
                                    {course.estimatedDuration ? (
                                        <span className="chip-out">
                                            {recommendationService.formatDuration(course.estimatedDuration)}
                                        </span>
                                    ) : null}
                                </div>
                                <h3>{course.title}</h3>
                                {primaryReason && (
                                    <div className="ideal">
                                        <MaterialSymbol name="star" size={16} />
                                        {primaryReason}
                                    </div>
                                )}
                                {rec.enrollmentMode === 'none' ? (
                                    <div className="contact">
                                        {translate('recommendations.contactAdmin', { _: 'Kontaktieren Sie Ihre Ansprechpartner' })}
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn-primary enroll"
                                        onClick={() => handleEnroll(course.id)}
                                    >
                                        {displayEnrollmentMode(rec.enrollmentMode) === 'request'
                                            ? translate('recommendations.requestEnroll', { _: 'Einschreibung anfragen' })
                                            : translate('recommendations.enroll', { _: 'Einschreiben' })}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RecommendedCoursesWidget;
